import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, CheckCircle2, X } from 'lucide-react';

export interface ToastState {
  kind: 'error' | 'success';
  message: string;
}

interface ToastProps {
  toast: ToastState | null;
  onDismiss: () => void;
}

/**
 * Portal to document.body — `position:fixed` inside MAX iframe / transformed
 * ancestors sticks to the document top (off-screen when user scrolled to submit).
 */
export function Toast({ toast, onDismiss }: ToastProps) {
  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(onDismiss, 4500);
    return () => window.clearTimeout(t);
  }, [toast, onDismiss]);

  useEffect(() => {
    if (!toast || toast.kind !== 'error') return;
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      window.scrollTo(0, 0);
    }
  }, [toast]);

  if (!toast) return null;
  const isError = toast.kind === 'error';

  return createPortal(
    <div
      className="nb-toast-host"
      role="status"
      aria-live="polite"
    >
      <div
        className={`nb-toast ${isError ? 'nb-toast--error' : 'nb-toast--success'}`}
      >
        {isError ? <AlertCircle size={18} className="shrink-0" /> : <CheckCircle2 size={18} className="shrink-0" />}
        <span className="flex-1 text-[14px] leading-snug text-ink">{toast.message}</span>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Закрыть"
          className="text-ink-faint hover:text-ink shrink-0"
        >
          <X size={16} />
        </button>
      </div>
    </div>,
    document.body,
  );
}
